import * as THREE from 'three';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { Camera } from '@/camera/Camera';

/**
 * SCREEN-SIZE VISIBILITY CULLING (2026-08-17, user: "focus on camera far mesh and focus on loading
 * near things … i get 30 fps on low-end laptops").
 *
 * Three.js already frustum-culls each mesh at draw time, so this is NOT about what's off to the
 * side — it's about what's technically in view but too far away to be worth drawing. A mesh whose
 * bounding sphere projects to a handful of pixels costs a full draw call, a state change and a
 * matrix upload to contribute a smudge, and this scene pays that cost **in every pass**: the cloud
 * depth prepass, the main render, the bloom pass, and one whole scene re-render per reflecting
 * water pool. Hiding it with `visible = false` removes it from all of them at once, and three skips
 * its entire subtree during traversal rather than testing it per pass.
 *
 * That draw-call overhead is exactly what hurts on the machines the user tested — integrated
 * graphics with a weak CPU, where per-draw driver cost dominates and there is no GPU headroom to
 * absorb it.
 *
 * WHY SCREEN SIZE AND NOT DISTANCE. A pixel threshold is self-scaling: it accounts for the mesh's
 * actual size and the current FOV, so one number behaves correctly for a pebble and for a whole
 * chapter's architecture. A raw distance cut would either pop large buildings out or leave small
 * props drawing forever. `maxDistance` is still available as a hard backstop, off by default.
 *
 * ⚠️ DELIBERATELY NOT FRUSTUM CULLING. Reflections render the scene from a MIRRORED camera, and
 * the bloom pass from the main one. Hiding by the main camera's frustum would make geometry
 * vanish out of the water reflections — a real visual bug. Distance and projected size are
 * position-based, so they stay valid for the mirror camera, which sits near the real one.
 *
 * COOPERATION WITH THE SCENE GATE. `World.setActiveScene`/`showSceneExclusive` also own `visible`.
 * They record their decision in `userData.sceneVisible`, and this class only ever computes
 * `visible = sceneGate && !culled` — so neither system can clobber the other.
 */

interface CullEntry {
  mesh: THREE.Mesh;
  /** The mesh's LOCAL bounding sphere, captured at registration — `LodSystem` may swap the
   *  geometry underneath us, so we don't re-read it from the live geometry each frame. */
  localCenter: THREE.Vector3;
  /** Local radius already multiplied by the mesh's world scale. */
  worldRadius: number;
  culled: boolean;
}

export class VisibilityCuller {
  private readonly entries: CullEntry[] = [];
  private readonly log = createLogger('cull');
  private accumulator = 0;
  private readonly _center = new THREE.Vector3();
  private readonly _scale = new THREE.Vector3();

  constructor(private readonly camera: Camera) {}

  /**
   * Register every plain, static mesh under `root`. `exclude` holds the meshes owned by another
   * system — moving arch stones, water surfaces, portal lights, vegetation planes, the emblem orb —
   * which must keep their own visibility rules.
   */
  build(root: THREE.Object3D, exclude: ReadonlySet<THREE.Object3D>): void {
    root.updateMatrixWorld(true);
    root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || exclude.has(mesh)) return;
      // Retired meshes (grass source planes, hidden originals) are permanently invisible — leave
      // them alone, or restoring the scene gate would pop them back into the frame.
      if (mesh.userData.permanentlyHidden) return;
      const geometry = mesh.geometry;
      if (!geometry) return;
      if (!geometry.boundingSphere) geometry.computeBoundingSphere();
      const sphere = geometry.boundingSphere;
      if (!sphere || sphere.radius <= 0) return;

      mesh.getWorldScale(this._scale);
      const scale = Math.max(Math.abs(this._scale.x), Math.abs(this._scale.y), Math.abs(this._scale.z));
      this.entries.push({
        mesh,
        localCenter: sphere.center.clone(),
        worldRadius: sphere.radius * scale,
        culled: false,
      });
    });
    this.log.info(`screen-size culling armed on ${this.entries.length} mesh(es).`);
  }

  /**
   * Re-evaluate every mesh, throttled to `updateIntervalSec`.
   *
   * The projected radius of a sphere at distance d, for a camera of vertical FOV f rendering to a
   * viewport h pixels tall, is `r / d * h / (2 tan(f/2))`. The trailing term is constant per frame,
   * so the per-mesh work is one matrix-transform and a divide.
   */
  update(delta: number, viewportHeight: number): void {
    const cfg = settings.visibilityCulling;
    // `minPixelSize <= 0` means the active quality tier wants NO culling (the HIGH tier does) —
    // treated exactly like the master switch being off, so everything hidden by us comes back.
    const threshold = cfg.minPixelSize;
    if (!cfg.enabled || threshold <= 0 || this.entries.length === 0) {
      if (this.restoreNeeded) this.restoreAll();
      return;
    }
    this.restoreNeeded = true;

    this.accumulator += delta;
    if (this.accumulator < cfg.updateIntervalSec) return;
    this.accumulator = 0;

    const camera = this.camera.instance;
    const camPos = camera.position;
    // Pixels per world-unit-at-unit-distance — the constant half of the projection above.
    const focalPx = viewportHeight / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5));
    const maxDistance = cfg.maxDistance > 0 ? cfg.maxDistance : Infinity;

    for (const entry of this.entries) {
      const mesh = entry.mesh;
      // World centre recomputed each pass: the GLB's baked animation moves nodes as the camera
      // scrubs, so a centre cached at load would drift out from under its mesh.
      this._center.copy(entry.localCenter).applyMatrix4(mesh.matrixWorld);
      const distance = camPos.distanceTo(this._center);

      let culled: boolean;
      if (distance > maxDistance) {
        culled = true;
      } else {
        // Distance to the sphere's SURFACE, floored just above zero — a camera inside the sphere
        // would otherwise divide by ~0 and report an absurd size (harmless, but this is clearer).
        const surface = Math.max(distance - entry.worldRadius, 0.001);
        const radiusPx = (entry.worldRadius / surface) * focalPx;
        // Hysteresis: a mesh already hidden must clear the threshold by 25% to come back, so a
        // camera drifting on the boundary can't flicker it on and off.
        culled = entry.culled ? radiusPx < threshold * 1.25 : radiusPx < threshold;
      }

      if (culled !== entry.culled) {
        entry.culled = culled;
        mesh.visible = !culled && this.sceneGateOf(mesh);
      } else if (!culled) {
        // Not culled: let the scene gate keep ownership (it may have changed since last pass).
        const gate = this.sceneGateOf(mesh);
        if (mesh.visible !== gate) mesh.visible = gate;
      }
    }
  }

  /** What `World`'s scene-reveal system wants for this mesh (default: visible). */
  private sceneGateOf(mesh: THREE.Mesh): boolean {
    const gate = mesh.userData.sceneVisible as boolean | undefined;
    return gate !== false;
  }

  /** Tracks whether anything is currently hidden BY US, so switching the system off from the GUI
   *  restores the scene exactly once rather than fighting the scene gate every frame. */
  private restoreNeeded = false;

  private restoreAll(): void {
    for (const entry of this.entries) {
      if (!entry.culled) continue;
      entry.culled = false;
      entry.mesh.visible = this.sceneGateOf(entry.mesh);
    }
    this.restoreNeeded = false;
  }

  /** Meshes currently hidden by distance / screen size — for the debug readout. */
  get culledCount(): number {
    let n = 0;
    for (const entry of this.entries) if (entry.culled) n += 1;
    return n;
  }

  get total(): number {
    return this.entries.length;
  }

  dispose(): void {
    this.restoreAll();
    this.entries.length = 0;
  }
}
